import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    History as HistoryIcon,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Autocomplete
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import suppliersService from '../services/suppliersService';
import { productsService } from '../services/api';

const SupplierEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';

    const [loading, setLoading] = useState(!isNew);
    const [activeTab, setActiveTab] = useState(0);
    const [supplier, setSupplier] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        website: '',
        contactName: '',
        notes: '',
    });

    // For products management
    const [supplierProducts, setSupplierProducts] = useState([]);
    const [openProductDialog, setOpenProductDialog] = useState(false);
    const [allProducts, setAllProducts] = useState([]); // List of products to select from
    const [selectedProduct, setSelectedProduct] = useState(null); // The product selected in autocomplete
    // const [productVariants, setProductVariants] = useState([]); // Variants of selected product
    const [dialogForm, setDialogForm] = useState({
        productId: null,
        productVariantId: null,
        supplierReference: '',
        price: '',
    });
    const [loadingProducts, setLoadingProducts] = useState(false);

    useEffect(() => {
        if (!isNew) {
            fetchSupplier();
        }
    }, [id]);

    const fetchSupplier = async () => {
        try {
            const data = await suppliersService.getSupplierById(id);
            setSupplier(data);
            setSupplierProducts(data.supplierProducts || []);
        } catch (error) {
            console.error('Erreur chargement fournisseur:', error);
            alert('Impossible de charger le fournisseur');
            navigate('/suppliers');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSupplier(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isNew) {
                const created = await suppliersService.createSupplier(supplier);
                navigate(`/suppliers/${created.id}`, { replace: true });
                // We could stay and show success, or go to list
            } else {
                await suppliersService.updateSupplier(id, supplier);
                fetchSupplier(); // Refresh
                alert('Fournisseur mis à jour');
            }
        } catch (error) {
            console.error('Erreur sauvegarde:', error);
            alert('Erreur lors de la sauvegarde');
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // --- Product Management ---

    const [editingProductId, setEditingProductId] = useState(null); // ID of the supplier-product link being edited

    const handleOpenAddProduct = async () => {
        setEditingProductId(null);
        setOpenProductDialog(true);
        setDialogForm({
            productId: null,
            productVariantId: null,
            supplierReference: '',
            price: ''
        });
        setSelectedProduct(null);

        // Load products for dropdown if not loaded
        if (allProducts.length === 0) {
            setLoadingProducts(true);
            try {
                const res = await productsService.getAll({ limit: 1000 });
                setAllProducts(res.data.products || res.data || []);
            } catch (err) {
                console.error("Error loading products", err);
            } finally {
                setLoadingProducts(false);
            }
        }
    };

    const handleEditProduct = (sp) => {
        setEditingProductId(sp.id);
        setDialogForm({
            productId: sp.productId, // Just for reference, won't change
            productVariantId: sp.productVariantId,
            supplierReference: sp.supplierReference || '',
            price: sp.price
        });
        // We don't necessarily need to set selectedProduct as we won't allow changing the product
        setOpenProductDialog(true);
    };

    const handleCloseProductDialog = () => {
        setOpenProductDialog(false);
        setEditingProductId(null);
    };

    const handleProductSelect = (event, value) => {
        setSelectedProduct(value);
        setDialogForm(prev => ({
            ...prev,
            productId: value ? value.id : null,
            productVariantId: null // Reset variant when product changes
        }));
    };

    const handleAddProductSubmit = async () => {
        try {
            if (!dialogForm.price) {
                alert("Veuillez saisir un prix");
                return;
            }

            if (editingProductId) {
                // Update existing
                await suppliersService.updateSupplierProduct(editingProductId, {
                    supplierReference: dialogForm.supplierReference,
                    price: parseFloat(dialogForm.price)
                });
            } else {
                // Create new
                if (!dialogForm.productId) {
                    alert("Veuillez sélectionner un produit");
                    return;
                }
                await suppliersService.addProductToSupplier(id, {
                    productId: dialogForm.productId,
                    productVariantId: dialogForm.productVariantId,
                    supplierReference: dialogForm.supplierReference,
                    price: parseFloat(dialogForm.price)
                });
            }

            setOpenProductDialog(false);
            fetchSupplier(); // Refresh list
        } catch (error) {
            console.error("Erreur sauvegarde produit", error);
            alert(error.response?.data?.message || "Erreur lors de la sauvegarde du produit");
        }
    };

    const handleRemoveProduct = async (supplierProductId) => {
        if (window.confirm("Retirer ce produit du fournisseur ?")) {
            try {
                await suppliersService.removeProductFromSupplier(supplierProductId);
                fetchSupplier();
            } catch (e) {
                console.error(e);
                alert("Erreur lors de la suppression");
            }
        }
    }

    if (loading) return <LoadingSpinner />;

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate('/suppliers')}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" component="h1" fontWeight="bold">
                    {isNew ? 'Nouveau fournisseur' : `Fournisseur: ${supplier.name}`}
                </Typography>
                {!isNew && (
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSubmit}
                        sx={{ ml: 'auto' }}
                    >
                        Enregistrer modifications
                    </Button>
                )}
            </Box>

            {!isNew && (
                <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
                    <Tab label="Informations Générales" />
                    <Tab label={`Produits Associés (${supplierProducts.length})`} />
                </Tabs>
            )}

            {/* TAB: GENERAL INFO */}
            {activeTab === 0 && (
                <Card>
                    <CardContent>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Nom du fournisseur"
                                        name="name"
                                        value={supplier.name}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Contact (Nom)"
                                        name="contactName"
                                        value={supplier.contactName || ''}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        name="email"
                                        type="email"
                                        value={supplier.email || ''}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Téléphone"
                                        name="phone"
                                        value={supplier.phone || ''}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Adresse"
                                        name="address"
                                        multiline
                                        rows={2}
                                        value={supplier.address || ''}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Site Web"
                                        name="website"
                                        value={supplier.website || ''}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Notes internes"
                                        name="notes"
                                        multiline
                                        rows={4}
                                        value={supplier.notes || ''}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                {isNew && (
                                    <Grid item xs={12}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            startIcon={<SaveIcon />}
                                        >
                                            Créer le fournisseur
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        </form>
                    </CardContent>
                </Card>
            )}

            {/* TAB: PRODUCTS */}
            {activeTab === 1 && !isNew && (
                <Box>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddProduct}>
                            Ajouter un produit
                        </Button>
                    </Box>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Image</TableCell>
                                    <TableCell>Produit</TableCell>
                                    <TableCell>Variante</TableCell>
                                    <TableCell>Référence Fournisseur</TableCell>
                                    <TableCell>Prix d'achat (HT)</TableCell>
                                    <TableCell>Dernier changement</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {supplierProducts.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">Aucun produit associé</TableCell>
                                    </TableRow>
                                ) : (
                                    supplierProducts.map((sp) => {
                                        const product = sp.product || sp.variant?.product;
                                        const image = product?.images?.find(i => i.isPrimary) || product?.images?.[0];
                                        const imageUrl = image ? `${process.env.REACT_APP_API_URL || 'https://api.inklandtattoo.fr'}${image.url}` : null;

                                        return (
                                            <TableRow key={sp.id}>
                                                <TableCell>
                                                    {imageUrl ? (
                                                        <Box
                                                            component="img"
                                                            src={imageUrl}
                                                            alt={product?.name}
                                                            sx={{ width: 50, height: 50, objectFit: 'contain', borderRadius: 1 }}
                                                        />
                                                    ) : (
                                                        <Box sx={{ width: 50, height: 50, bgcolor: 'grey.200', borderRadius: 1 }} />
                                                    )}
                                                </TableCell>
                                                <TableCell>{product?.name || 'Produit Inconnu'}</TableCell>
                                                <TableCell>{sp.variant ? sp.variant.name : 'Standard'}</TableCell>
                                                <TableCell>{sp.supplierReference || '-'}</TableCell>
                                                <TableCell>{sp.price} €</TableCell>
                                                <TableCell>
                                                    {sp.priceHistory && sp.priceHistory[0] ?
                                                        new Date(sp.priceHistory[0].changedAt).toLocaleDateString() : '-'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton color="primary" size="small" onClick={() => handleEditProduct(sp)} sx={{ mr: 1 }}>
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton color="error" size="small" onClick={() => handleRemoveProduct(sp.id)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* DIALOG ADD/EDIT PRODUCT */}
            <Dialog open={openProductDialog} onClose={handleCloseProductDialog} maxWidth="md" fullWidth>
                <DialogTitle>{editingProductId ? 'Modifier le produit' : 'Ajouter un produit au catalogue fournisseur'}</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {!editingProductId && (
                            <>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        options={allProducts}
                                        getOptionLabel={(option) => option.name || ''}
                                        loading={loadingProducts}
                                        value={selectedProduct}
                                        onChange={handleProductSelect}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Rechercher un produit" fullWidth required />
                                        )}
                                    />
                                </Grid>

                                {/* Variant Selector: Only show if product selected has variants */}
                                {selectedProduct && selectedProduct.variants && selectedProduct.variants.length > 0 && (
                                    <Grid item xs={12}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Variante (Optionnel si prix global)"
                                            value={dialogForm.productVariantId || ''}
                                            onChange={(e) => setDialogForm({ ...dialogForm, productVariantId: e.target.value })}
                                        >
                                            <MenuItem value=""><em>Appliquer au produit principal (si pas de variantes spécifiques)</em></MenuItem>
                                            {selectedProduct.variants.map((v) => (
                                                <MenuItem key={v.id} value={v.id}>
                                                    {v.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                )}
                            </>
                        )}

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Référence Fournisseur"
                                value={dialogForm.supplierReference}
                                onChange={(e) => setDialogForm({ ...dialogForm, supplierReference: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Prix d'achat HT"
                                type="number"
                                required
                                value={dialogForm.price}
                                onChange={(e) => setDialogForm({ ...dialogForm, price: e.target.value })}
                                InputProps={{
                                    endAdornment: <Typography>€</Typography>
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseProductDialog}>Annuler</Button>
                    <Button variant="contained" onClick={handleAddProductSubmit}>
                        {editingProductId ? 'Mettre à jour' : 'Ajouter'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SupplierEdit;
